import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import type { EInvoice } from '@/lib/api/einvoice-api'
import type { EInvoiceObservation } from '@/lib/api/einvoice-observation'
import { parseEInvoiceObservation } from '@/lib/api/einvoice-observation'
import { einvoiceKeys } from '@/hooks/use-einvoice-query'

export function invoiceIdentity(invoice?: EInvoice | null) {
    return JSON.stringify([
        invoice?.id,
        invoice?.ref_id,
        invoice?.inv_series,
        invoice?.transaction_id,
        invoice?.draft_revision,
        invoice?.status,
    ])
}

function matchesInvoice(
    observation: EInvoiceObservation,
    invoice?: EInvoice | null,
) {
    return (
        !invoice ||
        (invoice.id === observation.invoice_id &&
            (invoice.draft_revision === undefined ||
                invoice.draft_revision === observation.draft_revision) &&
            (observation.publication !== 'draft' ||
                ['draft', 'pushed', 'failed', 'publish_failed'].includes(
                    invoice.status,
                )) &&
            (['ref_id', 'inv_series', 'transaction_id'] as const).every(
                (field) =>
                    !invoice[field] ||
                    !observation[field] ||
                    invoice[field] === observation[field],
            ))
    )
}

export function storeEInvoiceObservation(
    client: QueryClient,
    orderId: string,
    value: unknown,
) {
    const observation = parseEInvoiceObservation(value, orderId)
    if (
        !observation ||
        !matchesInvoice(
            observation,
            client.getQueryData(einvoiceKeys.detail(orderId)),
        )
    )
        return
    client.setQueryData<EInvoiceObservation | null>(
        einvoiceKeys.observation(orderId),
        (previous) => {
            // Older, failed, or DB-backed reads cannot clear a verified provider warning.
            const sameIdentity =
                previous &&
                previous.invoice_id === observation.invoice_id &&
                previous.draft_revision === observation.draft_revision &&
                (['ref_id', 'inv_series', 'transaction_id'] as const).every(
                    (field) =>
                        !previous[field] ||
                        !observation[field] ||
                        previous[field] === observation[field],
                )
            if (
                sameIdentity &&
                previous.source === 'cloud_status' &&
                (observation.source !== 'cloud_status' ||
                    !observation.observed_at ||
                    (previous.observed_at !== null &&
                        Date.parse(observation.observed_at) <
                            Date.parse(previous.observed_at)))
            ) {
                return {
                    ...observation,
                    ref_id: observation.ref_id ?? previous.ref_id,
                    inv_series: observation.inv_series ?? previous.inv_series,
                    transaction_id:
                        observation.transaction_id ?? previous.transaction_id,
                    inv_no: observation.inv_no ?? previous.inv_no,
                    publication: previous.publication,
                    publication_code: previous.publication_code,
                    tax: previous.tax,
                    lifecycle: previous.lifecycle,
                    source: previous.source,
                    observed_at: previous.observed_at,
                }
            }
            return observation
        },
    )
}

export function invalidateEInvoiceDocument(
    client: QueryClient,
    orderId: string,
) {
    client.setQueryData(einvoiceKeys.observation(orderId), null)
    client.setQueryData<number>(
        einvoiceKeys.document(orderId),
        (generation = 0) => generation + 1,
    )
}

export function useEInvoiceObservation(
    orderId: string,
    invoice?: EInvoice | null,
) {
    const client = useQueryClient()
    const query = useQuery<EInvoiceObservation | null>({
        queryKey: einvoiceKeys.observation(orderId),
        queryFn: () => null,
        enabled: false,
        initialData: null,
    })
    const observation = query.data
    return observation &&
        matchesInvoice(
            observation,
            invoice ?? client.getQueryData(einvoiceKeys.detail(orderId)),
        )
        ? observation
        : null
}
